<template>
    <div class="order-detail-page" v-loading="loading">
        <div class="order-detail-page__topbar">
            <el-page-header content="订单详情" @back="handlePageBack" />
            <div class="order-detail-page__actions">
                <el-button type="primary" plain @click="$router.push('/orders/' + detail.id + '/receipt/edit')">编辑建档</el-button>
                <el-button plain @click="openDispatchDialog">外派分派</el-button>
                <el-button v-if="canReceiveDispatch" type="warning" plain @click="handleReceiveDispatch">接收外派</el-button>
                <el-button v-if="detail.dispatch && !detail.dispatch.recalled_at" type="danger" plain @click="handleRecallDispatch">撤回外派</el-button>
                <el-button plain @click="handlePrint">打印</el-button>
                <el-button @click="fetchDetail">刷新</el-button>
                <el-button
                    type="success"
                    :disabled="detail.is_archived === 1"
                    :loading="archiveLoading"
                    @click="handleArchive"
                >
                    {{ detail.is_archived === 1 ? '已归档' : '归档完成' }}
                </el-button>
            </div>
        </div>

        <el-row :gutter="20" class="order-detail-page__content">
            <el-col :span="16">
                <el-card shadow="never">
                    <div slot="header" class="order-detail-page__card-header">
                        <span>基础信息</span>
                        <el-tag :type="getTagType(detail.status_color)" size="small">
                            {{ detail.order_status_label || '--' }}
                        </el-tag>
                    </div>

                    <el-descriptions :column="2" border>
                        <el-descriptions-item label="订单编号">{{ detail.order_no || '--' }}</el-descriptions-item>
                        <el-descriptions-item label="颜色状态">{{ detail.status_color || '--' }}</el-descriptions-item>
                        <el-descriptions-item label="公司名称">{{ company.name || '--' }}</el-descriptions-item>
                        <el-descriptions-item label="统一信用代码">{{ company.credit_code || '--' }}</el-descriptions-item>
                        <el-descriptions-item label="法人姓名">{{ company.legal_name || '--' }}</el-descriptions-item>
                        <el-descriptions-item label="法人手机号">{{ company.phone || '--' }}</el-descriptions-item>
                        <el-descriptions-item label="法人身份证">{{ company.legal_id_no || '--' }}</el-descriptions-item>
                        <el-descriptions-item label="公司所属省份">{{ company.province || '--' }}</el-descriptions-item>
                        <el-descriptions-item label="公示结束时间">{{ company.announcement_end_date || '--' }}</el-descriptions-item>
                        <el-descriptions-item label="是否外勤">{{ detail.is_work_enabled === 1 ? '开启' : '关闭' }}</el-descriptions-item>
                        <el-descriptions-item v-if="detail.is_work_enabled === 1" label="合作外勤">{{ fieldPartnerLabel }}</el-descriptions-item>
                        <el-descriptions-item v-if="detail.is_work_enabled === 1" label="外勤费用">{{ fieldCostSummary }}</el-descriptions-item>
                    </el-descriptions>
                </el-card>

                <el-card shadow="never" class="order-detail-page__card">
                    <div slot="header" class="order-detail-page__card-header">
                        <span>外派信息</span>
                        <span class="order-detail-page__dispatch-status">
                            {{ detail.dispatch && !detail.dispatch.recalled_at ? '已分派' : '未分派' }}
                        </span>
                    </div>

                    <el-descriptions :column="2" border>
                        <el-descriptions-item label="分派人">{{ dispatchSenderName }}</el-descriptions-item>
                        <el-descriptions-item label="接收人">{{ dispatchReceiverName }}</el-descriptions-item>
                        <el-descriptions-item v-if="detail.dispatch && !detail.dispatch.recalled_at" label="外派范围">
                            {{ detail.dispatch.dispatch_scope_label || '--' }}
                        </el-descriptions-item>
                        <el-descriptions-item v-if="detail.dispatch && !detail.dispatch.recalled_at" label="外派费用">
                            {{ dispatchEmployeeCostSummary }}
                        </el-descriptions-item>
                        <el-descriptions-item label="分派时间">{{ detail.dispatch && detail.dispatch.dispatched_at ? detail.dispatch.dispatched_at : '--' }}</el-descriptions-item>
                        <el-descriptions-item label="接收时间">{{ detail.dispatch && detail.dispatch.received_at ? detail.dispatch.received_at : '--' }}</el-descriptions-item>
                        <el-descriptions-item label="撤回时间">{{ detail.dispatch && detail.dispatch.recalled_at ? detail.dispatch.recalled_at : '--' }}</el-descriptions-item>
                        <el-descriptions-item label="外派备注">{{ detail.dispatch && detail.dispatch.remark ? detail.dispatch.remark : '--' }}</el-descriptions-item>
                    </el-descriptions>
                </el-card>

                <el-card shadow="never" class="order-detail-page__card">
                    <div slot="header">流程节点</div>
                    <el-timeline>
                        <el-timeline-item
                            v-for="item in detail.process_tracks || []"
                            :key="item.id"
                            :timestamp="item.acted_at || item.updated_at"
                            :type="getTimelineType(item.process_status)"
                        >
                            <div class="order-detail-page__timeline-title">{{ item.process_name }}</div>
                            <div class="order-detail-page__timeline-remark">{{ item.remark || '暂无备注' }}</div>
                        </el-timeline-item>
                    </el-timeline>
                </el-card>

                <el-card id="order-process-flow" shadow="never" class="order-detail-page__card">
                    <div slot="header">流程办理</div>

                    <el-tabs v-model="activeTab">
                        <el-tab-pane label="税务注销" name="tax">
                            <el-form :model="taxForm" label-width="120px">
                                <el-form-item label="登录方式">
                                    <el-input v-model.trim="taxForm.tax_login_mode" placeholder="例如：电子税务局" />
                                </el-form-item>
                                <el-form-item label="办理进度">
                                    <el-select v-model="taxForm.tax_progress_status" class="full-width">
                                        <el-option label="清税中" :value="3" />
                                        <el-option label="已清税" :value="4" />
                                        <el-option label="税务未开" :value="1" />
                                    </el-select>
                                </el-form-item>
                                <el-form-item label="快登链接">
                                    <el-input v-model.trim="taxForm.tax_portal_url" />
                                </el-form-item>
                                <el-form-item label="备注">
                                    <el-input v-model.trim="taxForm.tax_note" type="textarea" :rows="3" />
                                </el-form-item>
                                <el-form-item>
                                    <el-button type="primary" :loading="submitLoading.tax" @click="submitTax">保存税务节点</el-button>
                                </el-form-item>
                            </el-form>
                        </el-tab-pane>

                        <el-tab-pane label="工商注销" name="biz">
                            <el-form :model="bizForm" label-width="120px">
                                <el-form-item label="办理进度">
                                    <el-select v-model="bizForm.biz_progress_status" class="full-width">
                                        <el-option label="待开始" :value="101" />
                                        <el-option label="办理中" :value="102" />
                                        <el-option label="已办结" :value="103" />
                                        <el-option label="异常" :value="104" />
                                    </el-select>
                                </el-form-item>
                                <el-form-item label="授权状态">
                                    <el-select v-model="bizForm.biz_authorization_status" class="full-width">
                                        <el-option label="未提供" :value="0" />
                                        <el-option label="已提供" :value="1" />
                                        <el-option label="异常" :value="2" />
                                    </el-select>
                                </el-form-item>
                                <el-form-item label="纸质执照">
                                    <el-select v-model="bizForm.biz_license_status" class="full-width">
                                        <el-option label="未提供" :value="0" />
                                        <el-option label="已提供" :value="1" />
                                        <el-option label="异常" :value="2" />
                                    </el-select>
                                </el-form-item>
                                <el-form-item label="年报情况">
                                    <el-select v-model="bizForm.biz_annual_report_status" class="full-width">
                                        <el-option label="未处理" :value="0" />
                                        <el-option label="已完成" :value="1" />
                                        <el-option label="异常" :value="2" />
                                    </el-select>
                                </el-form-item>
                                <el-form-item label="快登链接">
                                    <el-input v-model.trim="bizForm.biz_portal_url" />
                                </el-form-item>
                                <el-form-item label="备注">
                                    <el-input v-model.trim="bizForm.biz_note" type="textarea" :rows="3" />
                                </el-form-item>
                                <el-form-item>
                                    <el-button type="primary" :loading="submitLoading.biz" @click="submitBiz">保存工商节点</el-button>
                                </el-form-item>
                            </el-form>
                        </el-tab-pane>

                        <el-tab-pane label="外勤办理" name="work">
                            <el-alert
                                v-if="detail.is_work_enabled !== 1"
                                title="当前订单未开启外勤办理，后端会拒绝外勤写入。"
                                type="warning"
                                :closable="false"
                            />
                            <el-form v-else :model="workForm" label-width="120px">
                                <el-form-item label="外勤进度">
                                    <el-input-number v-model="workForm.work_progress_status" :min="1" :max="9" class="full-width" />
                                </el-form-item>
                                <el-form-item label="接收人ID">
                                    <el-input-number v-model="workForm.receiver_user_id" :min="1" class="full-width" />
                                </el-form-item>
                                <el-form-item label="线下办中">
                                    <el-switch v-model="workOfflineFlag" active-text="是" inactive-text="否" />
                                </el-form-item>
                                <el-form-item label="是否完成">
                                    <el-switch v-model="workCompletedFlag" active-text="已完成" inactive-text="进行中" />
                                </el-form-item>
                                <el-form-item label="外勤费用">
                                    <el-input-number v-model="workForm.work_fee" :min="0" :precision="2" class="full-width" />
                                </el-form-item>
                                <el-form-item label="办理记录">
                                    <el-input v-model.trim="workForm.work_content" type="textarea" :rows="3" />
                                </el-form-item>
                                <el-form-item>
                                    <el-button type="primary" :loading="submitLoading.work" @click="submitWork">保存外勤节点</el-button>
                                </el-form-item>
                            </el-form>
                        </el-tab-pane>
                    </el-tabs>
                </el-card>
            </el-col>

            <el-col :span="8">
                <el-card shadow="never">
                    <div slot="header">财务概览</div>
                    <el-descriptions :column="1" border>
                        <el-descriptions-item label="应收金额">{{ financial.receivable_total || 0 }}</el-descriptions-item>
                        <el-descriptions-item label="已收金额">{{ financial.paid_total || 0 }}</el-descriptions-item>
                        <el-descriptions-item label="待收金额">{{ financial.pending_amount != null && financial.pending_amount !== '' ? financial.pending_amount : (detail.pending_amount || 0) }}</el-descriptions-item>
                        <el-descriptions-item label="待收超期天数">{{ detail.pending_overdue_days || 0 }}</el-descriptions-item>
                    </el-descriptions>
                </el-card>

                <el-card shadow="never" class="order-detail-page__card">
                    <div slot="header">异常信息</div>
                    <el-empty v-if="!detail.system_exception_types || !detail.system_exception_types.length" description="暂无异常" :image-size="80" />
                    <el-tag
                        v-for="item in detail.system_exception_types || []"
                        :key="item"
                        type="danger"
                        class="order-detail-page__exception-tag"
                    >
                        {{ exceptionTextMap[item] || item }}
                    </el-tag>
                </el-card>

                <el-card id="remark-attachments" shadow="never" class="order-detail-page__card">
                    <div slot="header">备注与附件追加</div>
                    <el-form :model="remarkForm" label-width="88px">
                        <el-form-item label="备注类型">
                            <el-input v-model.trim="remarkForm.remark_type" placeholder="例如：客户沟通" />
                        </el-form-item>
                        <el-form-item label="流程节点">
                            <el-select v-model="remarkForm.related_process_node" class="full-width" clearable>
                                <el-option label="收件建档" value="receipt" />
                                <el-option label="税务注销" value="tax" />
                                <el-option label="工商注销" value="biz" />
                                <el-option label="外勤办理" value="work" />
                                <el-option label="归档完成" value="archive" />
                            </el-select>
                        </el-form-item>
                        <el-form-item label="备注内容">
                            <el-input v-model.trim="remarkForm.remark_content" type="textarea" :rows="3" />
                        </el-form-item>
                        <el-form-item label="附件上传">
                            <input
                                type="file"
                                multiple
                                accept=".pdf,.jpg,.jpeg"
                                @change="handleRemarkFilesChange"
                            >
                            <div class="order-detail-page__attachment-tip">仅支持 pdf/jpg/jpeg，最多 10 个，每个不超过 10MB。</div>
                            <div v-if="remarkFileNames.length" class="order-detail-page__attachment-list">
                                {{ remarkFileNames.join('，') }}
                            </div>
                        </el-form-item>
                        <el-form-item>
                            <el-button type="primary" :loading="remarkSubmitting" @click="submitRemark">追加备注</el-button>
                        </el-form-item>
                    </el-form>

                    <el-divider />

                    <div v-if="!remarks.length" class="order-detail-page__empty-text">暂无备注</div>
                    <div v-for="item in remarks" :key="item.id" class="order-detail-page__remark-item">
                        <div class="order-detail-page__remark-meta">
                            <span>{{ item.remark_type || '普通备注' }}</span>
                            <span>{{ item.related_process_node || '--' }}</span>
                            <span>{{ item.creator && item.creator.name ? item.creator.name : '--' }}</span>
                            <span>{{ item.created_at || '--' }}</span>
                        </div>
                        <div class="order-detail-page__remark-content">{{ item.remark_content }}</div>
                        <div v-if="item.attachments && item.attachments.length" class="order-detail-page__remark-files">
                            <el-button
                                v-for="file in item.attachments"
                                :key="file.id"
                                type="text"
                                size="mini"
                                @click="downloadAttachment(file)"
                            >
                                {{ file.file_name }}
                            </el-button>
                        </div>
                    </div>
                </el-card>
            </el-col>
        </el-row>

        <el-dialog title="外派分派" :visible.sync="dispatchDialogVisible" width="560px">
            <el-form :model="dispatchForm" label-width="100px">
                <el-form-item label="接收人员">
                    <el-select v-model="dispatchForm.receiver_user_id" class="full-width" filterable :loading="receiverOptionsLoading">
                        <el-option
                            v-for="user in receiverOptions"
                            :key="user.id"
                            :label="formatDispatchReceiverLabel(user)"
                            :value="user.id"
                        />
                    </el-select>
                </el-form-item>
                <el-form-item label="分派备注">
                    <el-input v-model.trim="dispatchForm.remark" type="textarea" :rows="3" />
                </el-form-item>
            </el-form>
            <span slot="footer">
                <el-button v-if="!isFieldCenterEntry" @click="dispatchDialogVisible = false">取消</el-button>
                <el-button type="primary" :loading="dispatchSubmitting" @click="submitDispatch">确认分派</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
export default {
    name: 'OrderDetail',
    data() {
        return {
            loading: false,
            archiveLoading: false,
            dispatchDialogVisible: false,
            dispatchSubmitting: false,
            remarkSubmitting: false,
            activeTab: 'tax',
            detail: {},
            receiverOptions: [],
            receiverOptionsLoading: false,
            remarkFiles: [],
            submitLoading: {
                tax: false,
                biz: false,
                work: false
            },
            dispatchForm: {
                receiver_user_id: null,
                remark: ''
            },
            remarkForm: {
                remark_type: '',
                related_process_node: '',
                remark_content: ''
            },
            taxForm: {
                tax_login_mode: '',
                tax_progress_status: 1,
                tax_note: '',
                tax_portal_url: ''
            },
            bizForm: {
                biz_progress_status: 101,
                biz_note: '',
                biz_portal_url: '',
                biz_authorization_status: 0,
                biz_license_status: 0,
                biz_annual_report_status: 0
            },
            workForm: {
                receiver_user_id: null,
                work_progress_status: 1,
                offline_in_progress_flag: 0,
                is_completed: 0,
                work_fee: 0,
                work_content: '',
                attachment_group_token: ''
            }
        };
    },
    created() {
        this.fetchDetail();
    },
    computed: {
        company() {
            return this.detail.company || {};
        },
        financial() {
            return this.detail.financial || {};
        },
        remarks() {
            return this.detail.remarks || [];
        },
        currentUser() {
            return this.$store.getters.currentUser || {};
        },
        dispatchSenderName() {
            return this.detail.dispatch && this.detail.dispatch.sender && this.detail.dispatch.sender.name
                ? this.detail.dispatch.sender.name
                : '--';
        },
        dispatchReceiverName() {
            return this.detail.dispatch && this.detail.dispatch.receiver && this.detail.dispatch.receiver.name
                ? this.detail.dispatch.receiver.name
                : '--';
        },
        fieldPartnerLabel() {
            var w = this.detail.work || {};
            var fp = w.field_partner;
            if (fp && fp.name) {
                return fp.phone ? String(fp.name) + ' ' + String(fp.phone) : String(fp.name);
            }
            return '--';
        },
        fieldCostSummary() {
            var f = this.financial;
            var pay = f.field_payable_total != null ? Number(f.field_payable_total) : 0;
            var paid = f.field_paid_total != null ? Number(f.field_paid_total) : 0;
            var pend = f.field_pending_total != null ? Number(f.field_pending_total) : Math.max(0, pay - paid);
            return '应付 ' + pay + ' / 已付 ' + paid + ' / 待付 ' + pend;
        },
        dispatchEmployeeCostSummary() {
            var d = this.detail.dispatch || {};
            var pay = d.employee_payable_total != null ? Number(d.employee_payable_total) : (d.dispatch_amount != null ? Number(d.dispatch_amount) : 0);
            var paid = d.employee_paid_total != null ? Number(d.employee_paid_total) : 0;
            var pend = d.employee_pending_total != null ? Number(d.employee_pending_total) : Math.max(0, pay - paid);
            return '应付 ' + pay + ' / 已付 ' + paid + ' / 待付 ' + pend;
        },
        canReceiveDispatch() {
            return !!(
                this.detail.dispatch &&
                !this.detail.dispatch.received_at &&
                !this.detail.dispatch.recalled_at &&
                this.currentUser &&
                this.currentUser.id === this.detail.dispatch.receiver_user_id
            );
        },
        remarkFileNames() {
            return this.remarkFiles.map(function (file) {
                return file.name;
            });
        },
        isFieldCenterEntry() {
            return this.$route.query && String(this.$route.query.from) === 'field-center';
        },
        workOfflineFlag: {
            get() {
                return this.workForm.offline_in_progress_flag === 1;
            },
            set(value) {
                this.workForm.offline_in_progress_flag = value ? 1 : 0;
            }
        },
        workCompletedFlag: {
            get() {
                return this.workForm.is_completed === 1;
            },
            set(value) {
                this.workForm.is_completed = value ? 1 : 0;
            }
        },
        exceptionTextMap() {
            return {
                announcement_overdue: '公示结束时间超期',
                financial_pending_overdue_over_30_days: '待收超期超过30天',
                financial_overpaid: '超额收款',
                offline_work_in_progress: '外勤线下办中',
                work_dispatch_not_updated_over_24_hours: '外勤24小时未更新',
                work_dispatch_not_updated_over_7_days: '外勤7天未更新'
            };
        }
    },
    watch: {
        '$route': {
            handler: function (to) {
                this.applyRouteFocus(to);
            },
            deep: true
        }
    },
    methods: {
        handlePageBack() {
            if (this.isFieldCenterEntry) {
                this.$router.push({ name: 'field-partners' });
                return;
            }
            this.$router.push('/todos');
        },
        scrollToRemarkAttachments() {
            var el = document.getElementById('remark-attachments');

            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        },
        scrollToProcessFlow() {
            var el = document.getElementById('order-process-flow');

            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        },
        applyRouteFocus(route) {
            var q = route && route.query && route.query.focus;

            if (!q) {
                return;
            }

            if (q === 'attachments') {
                this.$nextTick(this.scrollToRemarkAttachments);
                return;
            }

            if (q === 'biz' || q === 'biz_progress') {
                this.activeTab = 'biz';
                this.$nextTick(this.scrollToProcessFlow);
                return;
            }

            if (q === 'process' || q === 'status') {
                this.activeTab = 'tax';
                this.$nextTick(this.scrollToProcessFlow);
                return;
            }

            if (q === 'work') {
                this.activeTab = 'work';
                this.$nextTick(this.scrollToProcessFlow);
                return;
            }

            if (q === 'dispatch') {
                var _this = this;

                this.$nextTick(function () {
                    if (_this.detail && _this.detail.id) {
                        _this.openDispatchDialog();
                    }
                });
            }
        },
        fetchDetail() {
            var _this = this;
            this.loading = true;

            window.axios.get('/orders/' + this.$route.params.id).then(function (response) {
                _this.detail = response.data.data || {};
                _this.syncForms();
            }).catch(function () {
                _this.$message.error('订单详情加载失败');
            }).finally(function () {
                _this.loading = false;

                _this.applyRouteFocus(_this.$route);
            });
        },
        syncForms() {
            this.taxForm = {
                tax_login_mode: this.detail.tax_login_mode || '',
                tax_progress_status: this.detail.tax_progress_status || 1,
                tax_note: this.detail.tax_note || '',
                tax_portal_url: this.detail.tax_portal_url || ''
            };

            this.bizForm = {
                biz_progress_status: this.detail.biz_progress_status || 101,
                biz_note: this.detail.biz_note || '',
                biz_portal_url: this.detail.biz_portal_url || '',
                biz_authorization_status: this.detail.biz_authorization_status || 0,
                biz_license_status: this.detail.biz_license_status || 0,
                biz_annual_report_status: this.detail.biz_annual_report_status || 0
            };

            this.workForm = {
                receiver_user_id: this.detail.work && this.detail.work.receiver_user_id ? this.detail.work.receiver_user_id : null,
                work_progress_status: this.detail.work && this.detail.work.work_progress_status ? this.detail.work.work_progress_status : 1,
                offline_in_progress_flag: this.detail.work && this.detail.work.offline_in_progress_flag ? this.detail.work.offline_in_progress_flag : 0,
                is_completed: this.detail.work && this.detail.work.is_completed ? this.detail.work.is_completed : 0,
                work_fee: this.detail.work && this.detail.work.work_fee ? Number(this.detail.work.work_fee) : 0,
                work_content: this.detail.work && this.detail.work.work_content ? this.detail.work.work_content : '',
                attachment_group_token: this.detail.work && this.detail.work.attachment_group_token ? this.detail.work.attachment_group_token : ''
            };
        },
        openDispatchDialog() {
            var d = this.detail.dispatch || {};
            this.dispatchForm = {
                receiver_user_id: d.receiver_user_id ? Number(d.receiver_user_id) : null,
                remark: d.remark ? String(d.remark) : ''
            };
            this.dispatchDialogVisible = true;
            this.fetchReceivers();
        },
        fetchReceivers() {
            var _this = this;
            this.receiverOptionsLoading = true;

            window.axios.get('/dispatch/receivers', {
                params: { dispatch_scope: 0 }
            }).then(function (response) {
                _this.receiverOptions = response.data.data || [];
            }).catch(function () {
                _this.$message.error('接收人员加载失败');
            }).finally(function () {
                _this.receiverOptionsLoading = false;
            });
        },
        formatDispatchReceiverLabel(user) {
            var name = user && user.name ? String(user.name).trim() : '';
            var username = user && user.username ? String(user.username).trim() : '';
            var phone = user && user.phone ? String(user.phone).trim() : '';
            var displayName = name || username;

            if (!displayName) {
                return '--';
            }
            if (phone) {
                return displayName + '-' + phone;
            }
            return displayName;
        },
        submitDispatch() {
            var _this = this;
            this.dispatchSubmitting = true;

            window.axios.put('/orders/' + this.$route.params.id + '/dispatch', {
                receiver_user_id: this.dispatchForm.receiver_user_id,
                dispatch_scope: 0,
                remark: this.dispatchForm.remark || ''
            }).then(function () {
                _this.$message.success('外派分派成功');
                _this.dispatchDialogVisible = false;
                _this.fetchDetail();
            }).catch(function (error) {
                var message = error && error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : '外派分派失败';
                _this.$message.error(message);
            }).finally(function () {
                _this.dispatchSubmitting = false;
            });
        },
        handleReceiveDispatch() {
            var _this = this;

            window.axios.put('/orders/' + this.$route.params.id + '/dispatch/receive').then(function () {
                _this.$message.success('已接收外派订单');
                return _this.$store.dispatch('fetchCurrentUser').catch(function () {
                });
            }).then(function () {
                _this.fetchDetail();
            }).catch(function (error) {
                var message = error && error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : '接收外派失败';
                _this.$message.error(message);
            });
        },
        handleRecallDispatch() {
            var _this = this;

            this.$confirm('撤回后当前外派状态将失效，是否继续？', '确认撤回', {
                type: 'warning'
            }).then(function () {
                return window.axios.put('/orders/' + _this.$route.params.id + '/dispatch/recall').then(function () {
                    _this.$message.success('外派已撤回');
                    return _this.$store.dispatch('fetchCurrentUser').catch(function () {
                    });
                }).then(function () {
                    _this.fetchDetail();
                }).catch(function (error) {
                    var message = error && error.response && error.response.data && error.response.data.message
                        ? error.response.data.message
                        : '外派撤回失败';
                    _this.$message.error(message);
                });
            }).catch(function () {
            });
        },
        handleRemarkFilesChange(event) {
            var files = Array.prototype.slice.call(event.target.files || []);
            this.remarkFiles = files.slice(0, 10);
        },
        submitRemark() {
            var _this = this;
            var formData = new window.FormData();

            formData.append('remark_type', this.remarkForm.remark_type || '');
            formData.append('related_process_node', this.remarkForm.related_process_node || '');
            formData.append('remark_content', this.remarkForm.remark_content || '');

            this.remarkFiles.forEach(function (file) {
                formData.append('attachments[]', file);
            });

            this.remarkSubmitting = true;

            window.axios.post('/orders/' + this.$route.params.id + '/remarks', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            }).then(function (response) {
                _this.$message.success(response.data.message || '备注追加成功');
                _this.remarkForm = {
                    remark_type: '',
                    related_process_node: '',
                    remark_content: ''
                };
                _this.remarkFiles = [];
                _this.fetchDetail();
            }).catch(function (error) {
                var message = error && error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : '备注追加失败';
                _this.$message.error(message);
            }).finally(function () {
                _this.remarkSubmitting = false;
            });
        },
        downloadAttachment(file) {
            var url = '/orders/' + this.$route.params.id + '/remarks/attachments/' + file.id;

            window.axios.get(url, {
                responseType: 'blob'
            }).then(function (response) {
                var blobUrl = window.URL.createObjectURL(new Blob([response.data]));
                var link = document.createElement('a');
                link.href = blobUrl;
                link.download = file.file_name || 'attachment';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
            });
        },
        submitTax() {
            this.submitNode('tax', '/orders/' + this.$route.params.id + '/tax', this.taxForm, '税务节点保存成功');
        },
        submitBiz() {
            this.submitNode('biz', '/orders/' + this.$route.params.id + '/biz', this.bizForm, '工商节点保存成功');
        },
        submitWork() {
            this.submitNode('work', '/orders/' + this.$route.params.id + '/work', this.workForm, '外勤节点保存成功');
        },
        submitNode(type, url, payload, successMessage) {
            var _this = this;
            this.submitLoading[type] = true;

            window.axios.put(url, payload).then(function () {
                _this.$message.success(successMessage);
                _this.fetchDetail();
            }).catch(function (error) {
                var message = error && error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : '保存失败';
                _this.$message.error(message);
            }).finally(function () {
                _this.submitLoading[type] = false;
            });
        },
        handleArchive() {
            var _this = this;

            this.$confirm('归档后将进入已完成状态，是否继续？', '确认归档', {
                type: 'warning'
            }).then(function () {
                _this.archiveLoading = true;

                return window.axios.put('/orders/' + _this.$route.params.id + '/archive', {
                    remark: '前端归档操作'
                }).then(function () {
                    _this.$message.success('订单已归档');
                    _this.fetchDetail();
                }).catch(function (error) {
                    var message = error && error.response && error.response.data && error.response.data.message
                        ? error.response.data.message
                        : '归档失败';
                    _this.$message.error(message);
                }).finally(function () {
                    _this.archiveLoading = false;
                });
            }).catch(function () {
            });
        },
        handlePrint() {
            window.axios.get('/orders/' + this.$route.params.id + '/print', {
                responseType: 'text'
            }).then(function (response) {
                var printWindow = window.open('', '_blank');

                if (!printWindow) {
                    return;
                }

                printWindow.document.open();
                printWindow.document.write(response.data);
                printWindow.document.close();
                printWindow.focus();
                printWindow.print();
            });
        },
        getTagType(color) {
            var tagMap = {
                red: 'danger',
                yellow: 'warning',
                green: 'success'
            };

            return tagMap[color] || 'info';
        },
        getTimelineType(status) {
            var typeMap = {
                1: 'info',
                2: 'primary',
                3: 'success',
                4: 'danger'
            };

            return typeMap[status] || 'info';
        }
    }
};
</script>

<style scoped>
.order-detail-page {
    padding: 24px;
}

.order-detail-page__topbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
}

.order-detail-page__actions {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
}

.order-detail-page__content {
    margin-top: 16px;
}

.order-detail-page__card {
    margin-top: 20px;
}

.order-detail-page__card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.order-detail-page__timeline-title {
    font-weight: 600;
    color: #303133;
}

.order-detail-page__timeline-remark {
    margin-top: 6px;
    color: #606266;
}

.order-detail-page__exception-tag {
    margin-right: 8px;
    margin-bottom: 8px;
}

.order-detail-page__dispatch-status {
    color: #909399;
    font-size: 13px;
}

.order-detail-page__attachment-tip,
.order-detail-page__empty-text {
    color: #909399;
    font-size: 12px;
}

.order-detail-page__attachment-list {
    margin-top: 8px;
    color: #606266;
    font-size: 12px;
    word-break: break-all;
}

.order-detail-page__remark-item {
    padding: 12px 0;
    border-bottom: 1px solid #ebeef5;
}

.order-detail-page__remark-item:last-child {
    border-bottom: 0;
}

.order-detail-page__remark-meta {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    color: #909399;
    font-size: 12px;
}

.order-detail-page__remark-content {
    margin-top: 8px;
    color: #303133;
    line-height: 1.6;
}

.order-detail-page__remark-files {
    margin-top: 8px;
}

.full-width {
    width: 100%;
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/views/OrderDetail.vue